import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
    useRef,
} from "react";

import {
    Link,
    useNavigate,
    useParams,
    useRouteLoaderData,
    useSearchParams,
} from "@remix-run/react";
import clsx from "clsx";
import type { LucideIcon } from "lucide-react";
import {
    ChevronLeft,
    Copy,
    ImageIcon,
    X,
    FileText,
    Type,
    Lock,
    HeartHandshake,
    Trash2,
    Share2,
    Bell,
    MessageSquare,
    MoreVertical,
    Loader2,
    FolderTree,
    Terminal,
    Printer,
} from "lucide-react";
import { toast } from "sonner";

import { BillingSoftGate } from "~/components/billing/billing-soft-gate";
import { BillingAhaNudge } from "~/components/billing/billing-aha-nudge";
import { PractitionerReviewButton } from "~/components/session/practitioner-review-button";
import { getBrowserEnvVar } from "~/lib/env.shared";
import { Alert } from "~/components/ui/alert";
import { H2, P } from "~/components/ui/typography";
import { useSupabaseSession } from "~/context/SupabaseSessionContext";
import { TourRoot, useAutoTour } from "~/features/onboarding";
import type {
    TClientSettings,
    TPartInteractionWithImage,
    TSession,
} from "~/database/types";
import {
    ModelComparisonButton,
    type DeepseekShadowComparison,
    type ShadowParts,
} from "~/components/session-page/model-comparison-modal";
import { CommitmentsList } from "~/features/commitments/components";
import type { CommitmentListItem } from "~/features/commitments/types";
import { ManagePartDialog } from "~/features/parts/components/part-detail/manage-dialog";
import { PartDetailContent } from "~/features/parts/components/part-detail/part-detail-content";
import { PartImageEditorModal } from "~/features/parts/components/part-detail/part-image-editor-modal";
import { PartGroupSuggestionDialog } from "~/features/parts-grouping/components/part-group-suggestion-dialog";
import { GroupingType } from "~/features/parts-grouping/types";
import type { ProcessedRelationship } from "~/features/parts/utils/process-relationships";
import { aggregateSessionRelationships } from "~/features/parts/utils/session-part-relationships";
import { FeedbackSection } from "~/features/practitioner-feedback/components/feedback";
import { getSessionBillingBlockedPayload } from "~/features/billing/utils/session-billing-blocked";
import { useDeleteSession } from "~/hooks/mutations/use-delete-session";
import { useBillingEntitlement } from "~/hooks/queries/use-billing-entitlement";
import { useUserDetails } from "~/hooks/queries/use-user-details";
import { useViewerState } from "~/hooks/use-viewer-state";
import { useApiClient } from "~/hooks/use-api-client";
import { useCanDisplayPartImages } from "~/hooks/use-can-display-part-images";
import {
    usePartMutation,
    useSession,
    useSessionMutation,
} from "~/hooks/use-session";
import { useSessionResearchRelease } from "~/hooks/use-session-research-release";
import { trackEvent, trackEventAsUser } from "~/lib/analytics";
import { logger } from "~/lib/logger";
import { formatAttributeContent } from "~/lib/part-attributes";
import { Transcript } from "~/pages/TranscriptPage";

import { useIsPractitioner } from "../../lib/user";
import { formatDuration, getSessionAnnotationUrl } from "../../lib/utils";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "~/components/ui/dialog";
import {
    Drawer,
    DrawerContent,
    DrawerHeader,
    DrawerTitle,
} from "~/components/ui/drawer";
import { useIsMobile } from "~/hooks/use-mobile";

import { EditableContent } from "../editable-content";
import InternalFeature from "../internal-feature";
import Layout from "../layout";
import { DateEditor } from "./date-editor";
import { GenerateImagesBanner } from "./generate-images-banner";
import { SessionParticipants } from "./participant-selector";
import { ReminderDialog } from "./reminder-dialog";
import { ResearchReleaseDialog } from "./research-release-toggle";
import { SessionParts } from "./session-parts"; // Assuming SessionParts is the correct component name
import { UnifiedShareDialog } from "./unified-share-dialog";
import { TitleEditor } from "./title-editor";
import { ScopedPartsMap } from "~/features/packages/components/scoped-parts-map";
import { PromotionBanner } from "../session/promotion-banner";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuTrigger,
    DropdownMenuItem,
} from "../ui/dropdown-menu";

// AIDEV-NOTE: Tab configuration types and helpers for centralized tab management
type TabId = "summary" | "full-story" | "transcript" | "experiences" | "parts";

interface Tab {
    id: TabId;
    label: string;
    icon: LucideIcon;
    alwaysShow?: boolean;
    show?: boolean;
    mobileOnly?: boolean;
}

interface TabState {
    activeTab: TabId;
    showTranscript: boolean;
    showFullSummary: boolean;
}

const ENCODING_DEBUG_PATTERNS = [
    "\u00e2\u20ac\u201d", // â€”
    "\u00e2\u20ac\u2122", // â€™
    "\u00e2\u20ac\u0153", // â€œ
    "\u00e2\u20ac", // â€
    "\u00c3", // Ã
];

type EncodingDebugWindow = Window & {
    __ifsEncodingDebugOnce?: Record<string, boolean>;
};

const inspectEncoding = (value: string | null | undefined) => {
    if (!value) {
        return {
            hasValue: false,
            length: 0,
            hasMojibake: false,
            firstMojibakeIndex: -1,
            firstEmDashIndex: -1,
            snippet: "",
        };
    }

    const firstMojibakeIndex = ENCODING_DEBUG_PATTERNS.reduce(
        (index, pattern) => {
            const patternIndex = value.indexOf(pattern);
            if (patternIndex === -1) return index;
            if (index === -1) return patternIndex;
            return Math.min(index, patternIndex);
        },
        -1,
    );
    const firstEmDashIndex = value.indexOf("\u2014");
    const anchor =
        firstMojibakeIndex >= 0
            ? firstMojibakeIndex
            : firstEmDashIndex >= 0
              ? firstEmDashIndex
              : 0;
    const snippetStart = Math.max(0, anchor - 40);
    const snippet = value.slice(snippetStart, snippetStart + 140);

    return {
        hasValue: true,
        length: value.length,
        hasMojibake: firstMojibakeIndex >= 0,
        firstMojibakeIndex,
        firstEmDashIndex,
        snippet,
    };
};

const logEncodingOnce = (key: string, payload: Record<string, unknown>) => {
    if (typeof window === "undefined") return;
    const debugWindow = window as EncodingDebugWindow;
    debugWindow.__ifsEncodingDebugOnce ??= {};
    if (debugWindow.__ifsEncodingDebugOnce[key]) return;
    debugWindow.__ifsEncodingDebugOnce[key] = true;
    // AIDEV-NOTE: Temporary mojibake diagnostics. Remove after encoding investigation is complete.
    logger
        .withContext({ ...payload, encodingDebugKey: key })
        .info("ENCODING_DEBUG");
};

const tabStateMap: Record<TabId, Partial<TabState>> = {
    summary: { showTranscript: false, showFullSummary: false },
    "full-story": { showTranscript: false, showFullSummary: true },
    transcript: { showTranscript: true, showFullSummary: false },
    experiences: { showTranscript: false, showFullSummary: false },
    parts: { showTranscript: false, showFullSummary: false },
};

// Define the new SessionSummary component
interface SessionSummaryProps {
    session: TSession;
    sessionId: string;
    updateSessionSummary: (
        type: "short" | "long",
        value: string,
    ) => Promise<void>;
    showFullSummary: boolean;
    setShowFullSummary: (value: boolean) => void;
    isPublicSession?: boolean;
    commitments?: CommitmentListItem[];
}

const SessionSummary: React.FC<SessionSummaryProps> = ({
    session,
    sessionId,
    updateSessionSummary,
    showFullSummary,
    setShowFullSummary,
    isPublicSession = false,
    commitments = [],
}) => {
    // AIDEV-NOTE: The session payload carries isDemo (it is a column on `session`),
    // but the narrowed prop type in this file predates it and does not list it.
    // AIDEV-TODO: add isDemo to the shared session type and drop this narrowing.
    const isDemoSession = Boolean(
        (session as { isDemo?: boolean | null } | null | undefined)?.isDemo,
    );

    const shortSummaryValue = useMemo(
        () =>
            (session?.shortSummary || session?.rawReport?.summary)
                ?.replace(/\|/g, "\n\n")
                .trim(),
        [session?.shortSummary, session?.rawReport?.summary],
    );
    const longSummaryValue = useMemo(
        () => session?.longSummary?.replace(/\|/g, "\n\n").trim(),
        [session?.longSummary],
    );
    const deepseekComparison = session?.rawReport?.modelComparisons
        ?.deepseekV4Pro as DeepseekShadowComparison | undefined;
    const sonnetParts = session?.rawReport?.parts as ShadowParts | undefined;

    useEffect(() => {
        logEncodingOnce(`session-notes-input-${sessionId}`, {
            boundary: "SessionSummary.beforeEditableContent",
            sessionId,
            shortSummaryValue: inspectEncoding(shortSummaryValue),
            longSummaryValue: inspectEncoding(longSummaryValue),
            usingFullStoryTab: showFullSummary,
        });
    }, [sessionId, shortSummaryValue, longSummaryValue, showFullSummary]);

    // AIDEV-NOTE: The full story is DEFERRED behind a Read More link rather than owning a
    // tab. It is the same session told at length — a second reading, not a second subject —
    // so a tab gave it equal billing with the summary and split attention at the top of the
    // page. Modal on desktop, drawer on mobile.
    const [fullStoryOpen, setFullStoryOpen] = useState(false);
    const isMobile = useIsMobile();
    const fullStoryBody = (
        <EditableContent
            defaultValue={longSummaryValue}
            onSubmit={(value) => updateSessionSummary("long", value)}
            className=""
            disabled={isPublicSession || isDemoSession}
            debugLabel={`session-notes-long-${sessionId}`}
        />
    );

    return (
        <>
            <H2 className="mb-3 pb-0 text-left">Session Summary</H2>
            <div data-tour-id="summary-content">
                <EditableContent
                    defaultValue={shortSummaryValue}
                    onSubmit={(value) => updateSessionSummary("short", value)}
                    className=""
                    disabled={isPublicSession || isDemoSession}
                    debugLabel={`session-notes-short-${sessionId}`}
                />
            </div>
            {/* AIDEV-NOTE: Read more and the summary feedback share ONE row — they were
                stacked with my-8 between them, which spent a lot of vertical space on two
                small controls directly under the text they refer to. */}
            <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                {longSummaryValue ? (
                    <button
                        type="button"
                        onClick={() => setFullStoryOpen(true)}
                        className="text-sm font-semibold text-brand-terra-600 underline-offset-4 hover:underline"
                        data-testid="session-full-story-open"
                        data-tour-id="full-story-link"
                    >
                        Read more
                    </button>
                ) : (
                    <span />
                )}
                {!isPublicSession && (
                    <FeedbackSection
                        sessionId={sessionId}
                        text="Does this summary feel right to you?"
                        variant="subtle"
                    />
                )}
            </div>
            {isMobile ? (
                <Drawer open={fullStoryOpen} onOpenChange={setFullStoryOpen}>
                    <DrawerContent className="left-0">
                        <DrawerHeader className="text-left">
                            <DrawerTitle>Full story</DrawerTitle>
                        </DrawerHeader>
                        <div className="max-h-[70vh] overflow-y-auto px-4 pb-6">
                            {fullStoryBody}
                        </div>
                    </DrawerContent>
                </Drawer>
            ) : (
                <Dialog
                    analyticsId="session-full-story"
                    open={fullStoryOpen}
                    onOpenChange={setFullStoryOpen}
                >
                    <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle>Full story</DialogTitle>
                        </DialogHeader>
                        {fullStoryBody}
                    </DialogContent>
                </Dialog>
            )}
            {!isPublicSession && (
                <ModelComparisonButton
                    sessionId={sessionId}
                    sonnetShortSummary={shortSummaryValue}
                    sonnetLongSummary={longSummaryValue}
                    sonnetParts={sonnetParts}
                    comparison={deepseekComparison}
                />
            )}
        </>
    );
};

// AIDEV-NOTE: Print-only content component - splits parts by category (focal/mentioned/suspected)
// and honors effectiveSettings.hideSuspectedParts
interface PrintOnlyContentProps {
    session: TSession;
    parts: { present: TPartInteractionWithImage[]; other: unknown[] };
    effectiveSettings: TClientSettings;
}

// AIDEV-NOTE: Human-readable labels for part attributes in print view
const ATTRIBUTE_LABELS: Record<string, string> = {
    emotions: "Emotions",
    age: "Age",
    beliefsIntentionsAndFears: "Beliefs, Intentions & Fears",
    beliefsDesiresAndFears: "Beliefs, Desires & Fears",
    behavioralExpression: "Behavioral Expression",
    somaticExpression: "Somatic Expression",
    visualExpression: "Visual Expression",
    relationshipWithSelf: "Relationship with Self",
    burden: "Burden",
    mentioned: "Mentioned",
    relationships: "Relationships",
    expressions: "Expressions",
    quotes: "Quotes",
};

// AIDEV-NOTE: Attributes to skip in print (metadata, not content). The second group is folded
// into `attributes` by the v2 pipeline's extras whitelist (sessionPipelineV2) — pipeline
// bookkeeping and image-prompt drafts that were never meant for a client-facing printout.
const SKIP_ATTRIBUTES = new Set([
    "type",
    "quote",
    "recap",
    "evidenceLevel",
    "presenceLevel",
    "focusWeight",
    // AIDEV-NOTE: `quotes` is deliberately NOT skipped. The v2 prompt calls `.quote` a mirror of
    // `quotes[0]`, but nothing in the pipeline actually derives it — every production row that has
    // a `quotes` attribute has an empty `quote` column, so skipping it would drop the part's only
    // quote from the printout.
    "focusWeightSource",
    "focusPercentage",
    "firstAddressedBy",
    "imagePromptDraft",
    "imageStyleRequested",
]);

const PrintPartDetail: React.FC<{ part: TPartInteractionWithImage }> = ({
    part,
}) => {
    const partType =
        part.type || part.attributes?.find((a) => a.name === "type")?.content;
    const quote =
        part.quote || part.attributes?.find((a) => a.name === "quote")?.content;
    const recap =
        part.recap || part.attributes?.find((a) => a.name === "recap")?.content;

    // AIDEV-NOTE: Filter attributes to show all content attributes (excluding metadata and
    // already-shown ones). Content is flattened first because emotions/relationships can be
    // stored as JSON arrays — an empty array then filters out like any other blank field.
    const otherAttributes = (part.attributes || [])
        .filter((attr) => !SKIP_ATTRIBUTES.has(attr.name))
        .map((attr) => ({
            ...attr,
            content: formatAttributeContent(attr.content),
        }))
        .filter(
            (attr) =>
                attr.content &&
                attr.content !== "Unclear" &&
                attr.content.trim() !== "",
        );

    return (
        <div className="mt-6 break-inside-avoid border-t border-gray-200 pt-4">
            <h3 className="mb-1 text-[15px] font-semibold text-brand-forest-800">
                {part.part?.name || part.name}
            </h3>
            {partType && (
                <p className="mb-3 text-[12px] text-gray-500">{partType}</p>
            )}
            {quote && (
                <p className="mb-3 italic text-gray-700">
                    "{quote.replace(/"/g, "")}"
                </p>
            )}
            {recap && (
                <div className="mb-3">
                    <p className="mb-1 text-[12px] font-medium text-gray-500">
                        Session Recap
                    </p>
                    <p>{recap}</p>
                </div>
            )}
            {otherAttributes.map((attr) => (
                <div key={attr.name} className="mb-3">
                    <p className="mb-1 text-[12px] font-medium text-gray-500">
                        {ATTRIBUTE_LABELS[attr.name] || attr.name}
                    </p>
                    <p>{attr.content}</p>
                </div>
            ))}
        </div>
    );
};

const PrintOnlyContent: React.FC<PrintOnlyContentProps> = ({
    session,
    parts,
    effectiveSettings,
}) => {
    const focalParts = parts.present.filter(
        (part) => part.presenceLevel?.toLowerCase().includes("focal"),
    );
    const mentionedParts = parts.present.filter(
        (part) => part.presenceLevel?.toLowerCase().includes("mentioned"),
    );
    const suspectedParts = parts.present.filter(
        (part) =>
            part.presenceLevel?.toLowerCase().includes("suspected") &&
            part.evidenceLevel?.toLowerCase().includes("high"),
    );

    const showSuspected =
        !effectiveSettings?.hideSuspectedParts && suspectedParts.length > 0;

    return (
        <div className="hidden print:block print:mt-0 print:text-[13px]">
            <h2 className="mb-2 mt-0 text-[16px] font-semibold text-brand-forest-800">
                Summary
            </h2>
            <div className="prose prose-sm max-w-none">
                {(session?.shortSummary || session?.rawReport?.summary)
                    ?.replace(/\|/g, "\n\n")
                    .trim() || "No summary available"}
            </div>

            {/* Parts Overview - split by category (before Full Story) */}
            {focalParts.length > 0 && (
                <>
                    <h2 className="mb-2 mt-6 text-[16px] font-semibold text-brand-forest-800">
                        Parts you worked with
                    </h2>
                    <ul className="list-disc space-y-1 pl-5">
                        {focalParts.map((part) => (
                            <li key={part.id}>
                                <span className="font-medium">
                                    {part.part?.name || part.name}
                                </span>
                                {part.description && (
                                    <span className="text-gray-600">
                                        {" "}
                                        — {part.description}
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                </>
            )}

            {mentionedParts.length > 0 && (
                <>
                    <h2 className="mb-2 mt-6 text-[16px] font-semibold text-brand-forest-800">
                        Parts you mentioned
                    </h2>
                    <ul className="list-disc space-y-1 pl-5">
                        {mentionedParts.map((part) => (
                            <li key={part.id}>
                                <span className="font-medium">
                                    {part.part?.name || part.name}
                                </span>
                                {part.description && (
                                    <span className="text-gray-600">
                                        {" "}
                                        — {part.description}
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                </>
            )}

            {session?.longSummary && (
                <>
                    <h2 className="mb-2 mt-6 text-[16px] font-semibold text-brand-forest-800">
                        Full Story
                    </h2>
                    <div className="prose prose-sm max-w-none whitespace-pre-wrap">
                        {session.longSummary.replace(/\|/g, "\n\n").trim()}
                    </div>
                </>
            )}

            {showSuspected && (
                <>
                    <h2 className="mb-2 mt-6 text-[16px] font-semibold text-brand-forest-800">
                        Potential unaddressed parts
                    </h2>
                    <ul className="list-disc space-y-1 pl-5">
                        {suspectedParts.map((part) => (
                            <li key={part.id}>
                                <span className="font-medium">
                                    {part.part?.name || part.name}
                                </span>
                                {part.description && (
                                    <span className="text-gray-600">
                                        {" "}
                                        — {part.description}
                                    </span>
                                )}
                            </li>
                        ))}
                    </ul>
                </>
            )}

            {/* Detailed parts display - focal parts */}
            {focalParts.length > 0 && (
                <div className="mt-8">
                    <h2 className="mb-0 text-[16px] font-semibold text-brand-forest-800">
                        Part Details
                    </h2>
                    {focalParts.map((part) => (
                        <PrintPartDetail
                            key={`detail-${part.id}`}
                            part={part}
                        />
                    ))}
                </div>
            )}

            {/* Detailed parts display - mentioned parts */}
            {mentionedParts.length > 0 && (
                <div className="mt-8">
                    <h2 className="mb-0 text-[16px] font-semibold text-brand-forest-800">
                        Mentioned Part Details
                    </h2>
                    {mentionedParts.map((part) => (
                        <PrintPartDetail
                            key={`detail-${part.id}`}
                            part={part}
                        />
                    ))}
                </div>
            )}

            {/* Detailed parts display - suspected parts (if enabled) */}
            {showSuspected && (
                <div className="mt-8">
                    <h2 className="mb-0 text-[16px] font-semibold text-brand-forest-800">
                        Potential Unaddressed Part Details
                    </h2>
                    {suspectedParts.map((part) => (
                        <PrintPartDetail
                            key={`detail-${part.id}`}
                            part={part}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

// AIDEV-NOTE: The share-link entry point. It deliberately passes NO token: SessionNotes
// derives the viewer state itself (useViewerState) from the same search params, so there is
// exactly one place that decides "share-token view". The old `sessionToken` prop was already
// being ignored by SessionNotes — a second, silently-dead source of truth.
export const PublicSessionNotes: React.FC = () => {
    const { sessionId } = useParams();

    return <SessionNotes sessionId={sessionId || ""} />;
};

// AIDEV-NOTE: Renders in the Layout headerBanner slot so it sits above the
// eyebrow/title/actions row, not inside the page body.
const ExampleSessionBanner: React.FC = () => (
    <div className="border-brand-forest-200 bg-brand-cream-100 flex w-full items-center gap-3 rounded-[16px] border px-4 py-3">
        <span className="border-brand-forest-300 text-brand-forest-700 shrink-0 rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide">
            Example
        </span>
        <p className="text-muted-foreground text-sm">
            <span className="text-brand-forest-800 font-semibold">
                You&apos;re seeing this session exactly as your client sees it.
            </span>{" "}
            It&apos;s example data — explore anything; nothing here can break.
        </p>
    </div>
);

export const SessionNotes: React.FC<{
    sessionId: string;
    initialTab?: TabId;
}> = ({ sessionId, initialTab }) => {
    const apiClient = useApiClient();
    const [searchParams] = useSearchParams();
    const sessionToken = searchParams.get("sessionToken");
    // AIDEV-NOTE: ONE viewer state for this page. `isShareTokenView` answers "did they get
    // here through a share link" (read-only, non-owner) and `isAnonymousViewer` answers "is
    // there an account behind this viewer at all" — they are not interchangeable, and this
    // file previously mixed `!!sessionToken` with `useAnonymous()` and disagreed with itself
    // for a logged-in user opening a share link. See ~/hooks/use-viewer-state.
    const { isShareTokenView, isAnonymousViewer } = useViewerState();
    const isOnboardingDemo = searchParams.get("onboarding") === "1";
    const {
        session,
        parts,
        commitments,
        effectiveSettings,
        refetch,
        isLoading,
        showParticipantControls,
        isConsultantViewer,
    } = useSession(sessionId);
    // AIDEV-NOTE: A consultant with consultant-access to this session is a
    // SECOND kind of non-owner read-only viewer, alongside the share-token
    // case above — and a more dangerous one, because a consultant IS a real
    // practitioner-class account (Adam's design: consultants keep full
    // practitioner + client features for their own work), so bare
    // `isPractitioner` checks throughout this file do NOT exclude them. Every
    // write-affordance gate below was already keyed on `isShareTokenView`;
    // generalizing it to include `isConsultantViewer` closes both classes of
    // "logged-in non-owner" at once instead of auditing each site twice
    // (Adam, 2026-09-03 — consultants could otherwise edit date, practitioner/
    // client identity, summary, story, part name/type/merge/split, images).
    const isReadOnlyViewer = isShareTokenView || isConsultantViewer;
    const { userId } = useSupabaseSession();
    // AIDEV-NOTE: The example session is one shared row every practitioner is
    // linked to, so any edit here would rewrite it for all of them. The API
    // guards reject the write; this keeps the fields from looking editable.
    // AIDEV-TODO: add isDemo to the shared session type and drop this narrowing.
    const isDemoSession = Boolean(
        (session as { isDemo?: boolean | null } | null | undefined)?.isDemo,
    );
    const sessionOwnerId = session?.clientId || session?.uploadedByUserId;
    const { isPractitioner } = useIsPractitioner();
    const { data: userDetails } = useUserDetails();
    const { data: billingEntitlement } = useBillingEntitlement({
        enabled: !isReadOnlyViewer,
    });
    const { billingBlocked, blockedBySessionMarker } =
        getSessionBillingBlockedPayload({
            rawReport: session?.rawReport,
            entitlement: billingEntitlement?.entitlement,
        });
    const showSessionBillingBlockedBanner =
        session?.status === "ERROR_OTHER" &&
        blockedBySessionMarker &&
        !!billingBlocked;
    // AIDEV-NOTE: Check commitments setting (computed server-side)
    const showCommitments = isOnboardingDemo || userDetails?.showCommitments;

    const navigate = useNavigate();
    const [rightColumnContentType, setRightColumnContentType] = useState<
        "parts-list" | "part-detail"
    >("parts-list");
    const [selectedPartInteractionId, setSelectedPartInteractionId] = useState<
        string | null
    >(null);
    const [transcriptOpen, setTranscriptOpen] = useState(false);
    const [tabState, setTabState] = useState<TabState>({
        activeTab: initialTab || "summary",
        showTranscript: false,
        showFullSummary: false,
    });

    // AIDEV-NOTE: Unified tab state; screen view tracking centralized in AnalyticsScreenTracker
    const handleTabChange = useCallback(
        (tabId: TabId) => {
            if (tabId === "transcript") {
                trackEvent("viewed transcript tab", { sessionId });
            }
            const newState = tabStateMap[tabId];
            setTabState({
                activeTab: tabId,
                showTranscript: newState?.showTranscript ?? false,
                showFullSummary: newState?.showFullSummary ?? false,
            });
        },
        [sessionId],
    );
    const { mutateAsync: mutatePart } = usePartMutation(
        sessionId,
        sessionOwnerId,
    );
    const { mutateAsync: updateSession } = useSessionMutation(
        sessionId,
        sessionOwnerId,
        sessionToken,
    );
    const [isImageModalOpen, setIsImageModalOpen] = useState(false);
    const [isGroupSuggestionDialogOpen, setIsGroupSuggestionDialogOpen] =
        useState(false);
    const [groupSuggestionType, setGroupSuggestionType] =
        useState<GroupingType>(GroupingType.AREA_OF_LIFE);
    const { canDisplay: settingsCanDisplayPartImages } =
        useCanDisplayPartImages(sessionId);
    const canDisplayPartImages =
        isOnboardingDemo || settingsCanDisplayPartImages;

    // AIDEV-NOTE: The SESSION OWNER's meditation preference, resolved server-side in the
    // session route loader. Deliberately NOT useUserDetails().showMeditations — that is the
    // VIEWER's gate, and a practitioner is self-governed (EVO-1969), so a practitioner
    // reviewing a client's session saw meditation entry points they had disabled for that
    // client and could start one (Adam, 2026-09-09). Undefined (share-link view, or a
    // surface rendered outside this route) leaves PartMeditationButton on the viewer's own
    // gate, which is what token viewers and your own sessions want.
    const sessionRouteData = useRouteLoaderData(
        "routes/_protected.sessions.$sessionId",
    ) as { showMeditationsForClient?: boolean } | undefined;
    const meditationsEnabledForClient = isOnboardingDemo
        ? true
        : sessionRouteData?.showMeditationsForClient;

    useEffect(() => {
        if (
            session?.uploadedByUserId &&
            session.clientId !== userId &&
            session.clientId &&
            sessionToken
        ) {
            trackEventAsUser(
                "recipient viewed session",
                session.clientId ?? session.uploadedByUserId,
                {
                    session: sessionId,
                },
                apiClient,
            );
        }
    }, [
        session?.uploadedByUserId,
        userId,
        sessionId,
        session?.clientId,
        sessionToken,
    ]);

    // AIDEV-NOTE: Check if user has access to this session (client or practitioner)
    const isSessionOwner = session?.clientId === userId;
    const isSessionPractitioner = session?.practitionerId === userId;
    const hasSessionAccess = isSessionOwner || isSessionPractitioner;
    const canEditPartImage = canDisplayPartImages && !isReadOnlyViewer;

    // AIDEV-NOTE: Stable condition check for tour - pass function so it can be called when
    // needed. `userId` used to be listed here too, on the theory that the callback had to
    // update when auth loads — but hasSessionAccess is derived from it and is itself a
    // dependency, so it already carries that.
    const shouldShowTour = useCallback(() => {
        return (
            !isLoading &&
            hasSessionAccess &&
            !isReadOnlyViewer &&
            !!session &&
            parts.present.length > 0
        );
    }, [
        isLoading,
        hasSessionAccess,
        isReadOnlyViewer,
        session,
        parts.present.length,
    ]);

    useAutoTour("session-tour", shouldShowTour);

    const selectedPartInteraction = useMemo(
        () => parts.present.find((p) => p.id === selectedPartInteractionId),
        [selectedPartInteractionId, parts.present],
    );
    const selectedPart = useMemo(
        () =>
            selectedPartInteraction
                ? {
                      ...selectedPartInteraction.part,
                      part_interactions: [selectedPartInteraction],
                  }
                : null,
        [selectedPartInteraction],
    );

    // AIDEV-NOTE: Aggregate all relationships from all parts in the session and transform for PartsForceGraph
    // AIDEV-NOTE: Shared with the session-scoped part detail page, which draws the same
    // edges for one part's neighbourhood — see features/parts/utils/session-part-relationships.
    const aggregatedRelationships = useMemo(
        () => aggregateSessionRelationships(parts.present),
        [parts.present],
    );

    // AIDEV-NOTE: Filter parts to only include those with relationships for the force graph
    // AIDEV-NOTE: EVO-1224 - Deduplicate by partId to prevent same reusable part appearing multiple times
    // when multiple interactions in the same session link to the same reusable part
    const partsWithRelationships = useMemo(() => {
        if (aggregatedRelationships.length === 0) {
            return parts.present;
        }

        // Collect all part IDs that appear in relationships
        const partIdsInRelationships = new Set<string>();
        aggregatedRelationships.forEach((rel) => {
            partIdsInRelationships.add(rel.sourcePartId);
            partIdsInRelationships.add(rel.targetPartId);
        });

        // Filter parts to only those that have relationships
        const filteredParts = parts.present.filter((part) => {
            const partId = part.partId || part.id;
            return partIdsInRelationships.has(partId);
        });

        // Deduplicate by partId - multiple interactions may link to the same reusable part
        const seenPartIds = new Set<string>();
        const deduplicatedParts = filteredParts.filter((part) => {
            const partId = part.partId || part.id;
            if (seenPartIds.has(partId)) {
                return false;
            }
            seenPartIds.add(partId);
            return true;
        });

        return deduplicatedParts;
    }, [parts.present, aggregatedRelationships]);

    // AIDEV-NOTE: Practitioner-only map layer. These are Tier A suspected parts — entries
    // the detector inferred but nobody named aloud, carrying real Pass 1 ids and real
    // relationship rows, so they are drawable. (The synthesised suspectedParts[] bucket in
    // rawReport has no relationship data at all and is NOT this.) Practitioners scan for
    // exactly these after a session; clients find them confusing because they do not
    // recognise them, so the toggle never reaches a client surface, and it honours the
    // per-client hideSuspectedParts setting (Michelle Glass call, 2026-09-04).
    const suspectedMapParts = useMemo(
        () =>
            parts.present
                .filter(
                    (part) =>
                        part.presenceLevel
                            ?.toLowerCase()
                            .includes("suspected") &&
                        part.evidenceLevel?.toLowerCase().includes("high"),
                )
                .map((part) => ({
                    id: part.partId || part.id,
                    // AIDEV-NOTE: A partId-less interaction carries `part: {}` from the
                    // repository — fall back to the interaction's own detector name so the
                    // map never draws an unnamed node.
                    name: part.part.name || part.name,
                    type: part.part.type ?? null,
                    // AIDEV-NOTE: Same part-image display gate as regular map nodes —
                    // "Show suspected parts" must not leak images when they're off.
                    image: canDisplayPartImages
                        ? Array.isArray(part.part_image)
                            ? part.part_image[0]
                            : part.part_image
                        : null,
                })),
        [parts.present, canDisplayPartImages],
    );
    const allowSuspectedOnMap =
        isPractitioner && !effectiveSettings?.hideSuspectedParts;

    // AIDEV-NOTE: Calculate available tabs early to avoid hooks after conditional returns

    useEffect(() => {
        if (
            rightColumnContentType === "part-detail" &&
            !selectedPartInteraction
        ) {
            setRightColumnContentType("parts-list");
        }
    }, [rightColumnContentType, selectedPartInteraction]);
    // Clicking a part:
    const handlePartClick = useCallback(
        (part: TPartInteractionWithImage) => {
            // On mobile, preserve the old route-based behavior
            const mobileNavigate = () =>
                navigate(`/sessions/${sessionId}/parts/${part.id}`);
            // On desktop, show detail in the right panel
            const desktopNavigate = () => {
                setSelectedPartInteractionId(part.id);
                setRightColumnContentType("part-detail");
            };

            // Use feature detection for responsive behavior
            if (window.innerWidth < 1024) {
                // lg breakpoint
                mobileNavigate();
            } else {
                desktopNavigate();
            }
        },
        [navigate, sessionId],
    );

    const updateSessionSummary = async (
        type: "short" | "long",
        value: string,
    ) => {
        try {
            await updateSession({
                action: "update-summary",
                summaryType: type,
                content: value,
            });
        } catch (error) {
            logger
                .withError(
                    error instanceof Error ? error : new Error(String(error)),
                )
                .error("Error in updateSessionSummary");
        }
    };

    const updatePartName = async (partId: string, newName: string) => {
        try {
            await mutatePart({
                action: "rename",
                partId,
                newName,
            });

            refetch(); // Refresh session data to update the UI
        } catch (error) {
            toast.error("Failed to update part name");
        }
    };

    const handleDeleteComplete = () => {
        navigate("/sessions");
    };

    const isCurrentUserPractitioner = session?.practitionerId === userId;

    const [reminderOpenDesktop, setReminderOpenDesktop] = useState(false);
    const [openResearchDialogDesktop, setOpenResearchDialogDesktop] =
        useState(false);
    const [openDeleteDialogDesktop, setOpenDeleteDialogDesktop] =
        useState(false);
    const { mutate: deleteSession, isPending: isDeletingSession } =
        useDeleteSession();
    const {
        userHasResearchReleaseEnabled,
        sessionHasResearchReleaseEnabled,
        effectiveResearchReleaseEnabled,
        isLoading: isLoadingResearchRelease,
    } = useSessionResearchRelease({ sessionId });

    const [shareDialogOpen, setShareDialogOpen] = useState(false);
    const [isCopyingAnnotationLink, setIsCopyingAnnotationLink] =
        useState(false);

    // AIDEV-NOTE: Dev-only menu items for debugging - show in non-production environments
    const envName = getBrowserEnvVar("ENV_NAME");
    const isDevMode = envName !== "production";
    const canShareForAnnotation =
        !!userDetails?.isAdmin && !isReadOnlyViewer && !!session;

    const copyAnnotationLink = useCallback(async () => {
        if (!canShareForAnnotation) {
            return;
        }

        setIsCopyingAnnotationLink(true);
        try {
            const response = await apiClient.post("/admin/sessionToken", {
                sessionId,
                nonExpiring: true,
            });
            if (!response.ok) {
                const data = (await response.json().catch(() => null)) as {
                    error?: string;
                } | null;
                toast.error(data?.error || "Failed to create annotation link");
                return;
            }

            const data = (await response.json()) as { token?: string };
            if (!data.token) {
                toast.error("Failed to create annotation link");
                return;
            }

            // AIDEV-NOTE: Use the current domain so the copied link points at whatever env
            // the admin is actually on, not the SERVER_URL env var (staging/prod canonical host).
            const annotationUrl = getSessionAnnotationUrl(
                sessionId,
                data.token,
                window.location.origin,
            );
            await navigator.clipboard.writeText(annotationUrl);
            toast.success("Annotation link copied to clipboard");
        } catch (error) {
            logger
                .withError(
                    error instanceof Error ? error : new Error(String(error)),
                )
                .error("Failed to copy annotation link");
            toast.error("Failed to copy annotation link");
        } finally {
            setIsCopyingAnnotationLink(false);
        }
    }, [apiClient, canShareForAnnotation, sessionId]);

    const renderAnnotationShareMenuItem = () =>
        canShareForAnnotation ? (
            <DropdownMenuItem
                onSelect={(e) => {
                    e.preventDefault();
                    void copyAnnotationLink();
                }}
                disabled={isCopyingAnnotationLink}
                className="flex cursor-pointer items-center gap-2"
            >
                {isCopyingAnnotationLink ? (
                    <Loader2 className="size-4 animate-spin" />
                ) : (
                    <FileText className="size-4" />
                )}
                <span>Share for annotation</span>
            </DropdownMenuItem>
        ) : null;

    const sessionMenuItems =
        !isReadOnlyViewer && isSessionOwner ? (
            <>
                <DropdownMenuItem
                    onSelect={(e) => {
                        e.preventDefault();
                        setShareDialogOpen(true);
                    }}
                    className="flex cursor-pointer items-center gap-2"
                >
                    <Share2 className="size-4" />
                    <span>Share</span>
                </DropdownMenuItem>
                {renderAnnotationShareMenuItem()}
                <DropdownMenuItem
                    onSelect={() => {
                        trackEvent("printed session", { sessionId });
                        window.print();
                    }}
                    className="flex cursor-pointer items-center gap-2"
                >
                    <Printer className="size-4" />
                    <span>Print</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                    onSelect={(e) => {
                        e.preventDefault();
                        deleteSession(
                            { sessionId },
                            {
                                onSuccess: () => {
                                    handleDeleteComplete();
                                },
                            },
                        );
                    }}
                    className="text-destructive flex cursor-pointer items-center gap-2"
                >
                    <Trash2 className="size-4" />
                    <span>Delete Session</span>
                </DropdownMenuItem>
                {isDevMode && (
                    <DropdownMenuItem asChild>
                        <Link
                            to={`/sessions/${sessionId}/pipeline-output`}
                            className="flex cursor-pointer items-center gap-2"
                        >
                            <Terminal className="size-4" />
                            <span>Pipeline Output</span>
                        </Link>
                    </DropdownMenuItem>
                )}
            </>
        ) : isPractitioner && session && !isReadOnlyViewer ? (
            <>
                <DropdownMenuItem
                    onSelect={(e) => {
                        e.preventDefault();
                        setShareDialogOpen(true);
                    }}
                    className="flex cursor-pointer items-center gap-2"
                >
                    <Share2 className="size-4" />
                    <span>Share</span>
                </DropdownMenuItem>
                {renderAnnotationShareMenuItem()}
                <DropdownMenuItem
                    onSelect={() => {
                        trackEvent("printed session", { sessionId });
                        window.print();
                    }}
                    className="flex cursor-pointer items-center gap-2"
                >
                    <Printer className="size-4" />
                    <span>Print</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                    onSelect={(e) => {
                        e.preventDefault();
                        deleteSession(
                            { sessionId },
                            {
                                onSuccess: () => {
                                    handleDeleteComplete();
                                },
                            },
                        );
                    }}
                    className="text-destructive flex cursor-pointer items-center gap-2"
                >
                    <Trash2 className="size-4" />
                    <span>Delete Session</span>
                </DropdownMenuItem>
                {isDevMode && (
                    <DropdownMenuItem asChild>
                        <Link
                            to={`/sessions/${sessionId}/pipeline-output`}
                            className="flex cursor-pointer items-center gap-2"
                        >
                            <Terminal className="size-4" />
                            <span>Pipeline Output</span>
                        </Link>
                    </DropdownMenuItem>
                )}
            </>
        ) : undefined;

    // AIDEV-NOTE: Impression counterpart to "consultation cta clicked". Fires once
    // per session view where a practitioner could actually see the CTA — without
    // the denominator, click counts can't answer whether the button converts.
    const consultationCtaVisible = Boolean(
        isPractitioner &&
            session &&
            !isReadOnlyViewer &&
            !isSessionOwner &&
            (!isOnboardingDemo ||
                userDetails?.practitionerInitialOnboarding?.step ===
                    "demo_consultation"),
    );
    useEffect(() => {
        if (!consultationCtaVisible) return;
        trackEvent("consultation cta viewed", { sessionId });
    }, [consultationCtaVisible, sessionId]);

    const sessionActions =
        !isReadOnlyViewer && isSessionOwner ? (
            <>
                {/* Desktop actions - hidden in print */}
                {/* AIDEV-NOTE: Reminders are gated on `isAnonymousViewer`, NOT on
                    `isReadOnlyViewer` — a reminder needs an account with an email to send to.
                    The three ReminderDialog sites below use the same predicate on purpose. */}
                <div className="hidden items-center gap-2 lg:flex print:hidden">
                    {!isAnonymousViewer && (
                        <div data-tour-id="reminder-button">
                            <ReminderDialog
                                sessionId={sessionId}
                                sessionName={session.title!}
                                effectiveSettings={effectiveSettings}
                            />
                        </div>
                    )}
                    <UnifiedShareDialog
                        sessionId={sessionId}
                        showLinkOption={false}
                    />
                    {/* Group sensitive actions into a desktop dropdown */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="default"
                                size="icon"
                                aria-label="More actions"
                                data-tour-id="session-actions-menu"
                            >
                                <MoreVertical className="size-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-56">
                            {!!session?.transcript && (
                                <DropdownMenuItem
                                    onSelect={(e) => {
                                        e.preventDefault();
                                        setTranscriptOpen(true);
                                    }}
                                    className="cursor-pointer"
                                    data-tour-feature="transcript"
                                >
                                    <Type className="mr-2 size-4" />
                                    <span>Transcript</span>
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                                onSelect={(e) => {
                                    e.preventDefault();
                                    setOpenResearchDialogDesktop(true);
                                }}
                                className="text-brand-terra-500 cursor-pointer"
                            >
                                {isLoadingResearchRelease ? (
                                    <>
                                        <Loader2 className="mr-2 size-4 animate-spin" />
                                        <span>Loading...</span>
                                    </>
                                ) : effectiveResearchReleaseEnabled ? (
                                    <>
                                        <HeartHandshake className="mr-2 size-4" />
                                        <span>Donated</span>
                                    </>
                                ) : (
                                    <>
                                        <Lock className="mr-1 size-4" />
                                        <span>Private</span>
                                    </>
                                )}
                            </DropdownMenuItem>
                            {renderAnnotationShareMenuItem()}
                            <DropdownMenuItem
                                onSelect={(e) => {
                                    e.preventDefault();
                                    setOpenDeleteDialogDesktop(true);
                                }}
                                className="text-destructive cursor-pointer"
                            >
                                <Trash2 className="mr-2 size-4" />
                                <span>Delete Session</span>
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                    {/* Controlled dialog mounted once at root */}
                    {!isAnonymousViewer && (
                        <ReminderDialog
                            sessionId={sessionId}
                            sessionName={session.title!}
                            effectiveSettings={effectiveSettings}
                            open={reminderOpenDesktop}
                            onOpenChange={setReminderOpenDesktop}
                            hideTrigger
                        />
                    )}
                    <ResearchReleaseDialog
                        sessionId={sessionId}
                        open={openResearchDialogDesktop}
                        onOpenChange={setOpenResearchDialogDesktop}
                    />
                    <AlertDialog
                        open={openDeleteDialogDesktop}
                        onOpenChange={setOpenDeleteDialogDesktop}
                    >
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>
                                    Delete Session
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                    Are you sure you want to delete this
                                    session? This action cannot be undone.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel disabled={isDeletingSession}>
                                    Cancel
                                </AlertDialogCancel>
                                <AlertDialogAction
                                    onClick={() =>
                                        deleteSession(
                                            { sessionId },
                                            {
                                                onSuccess: () => {
                                                    setOpenDeleteDialogDesktop(
                                                        false,
                                                    );
                                                    handleDeleteComplete();
                                                },
                                            },
                                        )
                                    }
                                    disabled={isDeletingSession}
                                    className="bg-destructive hover:bg-destructive/90 text-white"
                                >
                                    {isDeletingSession
                                        ? "Deleting..."
                                        : "Delete"}
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </div>
            </>
        ) : isPractitioner && session && !isReadOnlyViewer ? (
            <>
                {/* Desktop actions - hidden in print */}
                <div className="hidden items-center gap-2 lg:flex print:hidden">
                    {/* AIDEV-NOTE: Practitioner-only consultation CTA. Deliberately in
                        this branch and not the session-owner one — session notes are
                        shared with clients and this must never render in the client
                        view. Solid terra for prominence; if it sticks, promote it to a
                        `solidTerra` button variant rather than leaving the override. */}
                    <Button
                        asChild
                        variant="default"
                        className="border-brand-terra-600 bg-brand-terra-600 text-white hover:bg-brand-terra-700 hover:text-white"
                        data-tour-id="consultation-button"
                    >
                        <Link
                            to={`/reflections/${sessionId}`}
                            onClick={() =>
                                trackEvent("consultation cta clicked", {
                                    sessionId,
                                })
                            }
                            className="flex items-center gap-2"
                        >
                            <MessageSquare className="size-4" />
                            <span>
                                {isDemoSession
                                    ? "See consultation"
                                    : "Consultation"}
                            </span>
                        </Link>
                    </Button>
                    {isCurrentUserPractitioner && session && (
                        <div className="ml-2">
                            <PractitionerReviewButton
                                sessionId={sessionId}
                                needsPractitionerReview={
                                    !!session.needsPractitionerReview
                                }
                                isSessionPractitioner={
                                    isCurrentUserPractitioner
                                }
                                isOwnSession={
                                    session.practitionerId === session.clientId
                                }
                            />
                        </div>
                    )}
                    <UnifiedShareDialog
                        sessionId={sessionId}
                        showLinkOption={true}
                    />
                    {/* AIDEV-NOTE: Duplicate data-tour-id across the two action rows is
                        safe — tours resolve with querySelectorAll and take the visible one,
                        so the CSS-hidden branch is skipped. Same contract the commitments
                        anchor already relies on. */}
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="outline"
                                size="icon"
                                aria-label="More actions"
                                data-tour-id="session-actions-menu"
                            >
                                <MoreVertical className="size-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-60">
                            {!!session?.transcript && (
                                <DropdownMenuItem
                                    onSelect={(e) => {
                                        e.preventDefault();
                                        setTranscriptOpen(true);
                                    }}
                                    className="cursor-pointer"
                                    data-tour-feature="transcript"
                                >
                                    <Type className="mr-2 size-4" />
                                    <span>Transcript</span>
                                </DropdownMenuItem>
                            )}
                            {/* AIDEV-NOTE: Reminder moved out of the action row into
                                this menu for practitioners. It is driven by controlled
                                state rather than a DialogTrigger — a menu item closes
                                the menu on select and swallows the trigger click. */}
                            {!isAnonymousViewer && (
                                <DropdownMenuItem
                                    data-tour-id="reminder-button"
                                    onSelect={(e) => {
                                        e.preventDefault();
                                        setReminderOpenDesktop(true);
                                    }}
                                    className="cursor-pointer"
                                >
                                    <Bell className="mr-2 size-4" />
                                    <span>Remind</span>
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                                onSelect={(e) => {
                                    e.preventDefault();
                                    setOpenResearchDialogDesktop(true);
                                }}
                                className="cursor-pointer"
                            >
                                <HeartHandshake className="mr-2 size-4" />
                                <span>Privacy / Donate</span>
                            </DropdownMenuItem>
                            {renderAnnotationShareMenuItem()}
                            <DropdownMenuItem
                                onSelect={(e) => {
                                    e.preventDefault();
                                    setOpenDeleteDialogDesktop(true);
                                }}
                                className="text-destructive cursor-pointer"
                            >
                                <Trash2 className="mr-2 size-4" />
                                <span>Delete Session</span>
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                    <ResearchReleaseDialog
                        sessionId={sessionId}
                        open={openResearchDialogDesktop}
                        onOpenChange={setOpenResearchDialogDesktop}
                    />
                    <AlertDialog
                        open={openDeleteDialogDesktop}
                        onOpenChange={setOpenDeleteDialogDesktop}
                    >
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>
                                    Delete Session
                                </AlertDialogTitle>
                                <AlertDialogDescription>
                                    Are you sure you want to delete this
                                    session? This action cannot be undone.
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel disabled={isDeletingSession}>
                                    Cancel
                                </AlertDialogCancel>
                                <AlertDialogAction
                                    onClick={() =>
                                        deleteSession(
                                            { sessionId },
                                            {
                                                onSuccess: () => {
                                                    setOpenDeleteDialogDesktop(
                                                        false,
                                                    );
                                                    handleDeleteComplete();
                                                },
                                            },
                                        )
                                    }
                                    disabled={isDeletingSession}
                                    className="bg-destructive hover:bg-destructive/90 text-white"
                                >
                                    {isDeletingSession
                                        ? "Deleting..."
                                        : "Delete"}
                                </AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </div>
            </>
        ) : undefined;

    const copyTranscriptToClipboard = useCallback(() => {
        if (!session?.transcript) return;

        // AIDEV-NOTE: Format transcript for clipboard with speaker labels and timestamps
        const transcriptText = session.transcript
            .map((message) => {
                const speakerLabel =
                    session.speakerLabels?.find(
                        (label) => label.value === message.speaker.trim(),
                    )?.label || `Speaker ${message.speaker.trim()}`;
                const timestamp = formatDuration(message.startTime / 1000);
                return `${speakerLabel} (${timestamp}):\n${message.content}`;
            })
            .join("\n\n");

        navigator.clipboard
            .writeText(transcriptText)
            .then(() => {
                toast.success("Transcript copied to clipboard!");
            })
            .catch((err) => {
                logger
                    .withError(err)
                    .error("Failed to copy transcript to clipboard");
                toast.error("Failed to copy transcript.");
            });
    }, [session?.transcript, session?.speakerLabels]);

    if (isLoading) {
        return <Layout loading={true} />;
    }

    if (!session) {
        return (
            <Layout title="Session Not Found">
                <P>
                    This session either doesn't exist or you don't have access
                    to it.
                </P>
                <Button onClick={() => navigate("/sessions")} variant="subtle">
                    Go back
                </Button>
            </Layout>
        );
    }

    let rightColumnContent: React.ReactNode = undefined;
    let rightColumnTitle: React.ReactNode = undefined;
    // Desktop only content
    if (rightColumnContentType === "part-detail" && selectedPartInteraction) {
        rightColumnContent = (
            <PartDetailContent
                sessionId={sessionId}
                partInteraction={selectedPartInteraction}
                inlineTitle={true}
                sessionOwnerId={sessionOwnerId}
                isPublicSession={isReadOnlyViewer}
                showPartImages={canDisplayPartImages}
                meditationsEnabledForClient={meditationsEnabledForClient}
                onImageClick={() => setIsImageModalOpen(true)}
                relationships={
                    "relationships" in selectedPartInteraction
                        ? (selectedPartInteraction.relationships as ProcessedRelationship[])
                        : undefined
                }
                partId={selectedPartInteraction.partId || undefined}
                onPartUpdated={() => refetch()}
            />
        );
        rightColumnTitle = (
            <EditableContent
                defaultValue={
                    selectedPartInteraction.part?.name ||
                    selectedPartInteraction.name
                }
                onSubmit={(newName) =>
                    updatePartName(selectedPartInteraction.partId!, newName)
                }
                className="font-display text-brand-forest-800 grow py-0 text-2xl font-semibold leading-tight"
                disabled={isReadOnlyViewer || isDemoSession}
            />
        );
    } else {
        rightColumnContent = (
            <div className="space-y-12">
                <SessionParts
                    meditationsEnabledForClient={meditationsEnabledForClient}
                    parts={{
                        present: parts.present,
                        other: parts.other,
                        suspectedParts: parts.suspectedParts,
                    }}
                    handlePartClick={handlePartClick}
                    sessionId={sessionId}
                    partSimilarities={session?.rawReport?.partSimilarities}
                    // AIDEV-NOTE: SessionParts has no readOnly concept of its own —
                    // isPractitioner IS its edit-affordance gate throughout (rename,
                    // merge, split, type change). A consultant satisfies isPractitioner
                    // (practitioner-class account) but must not edit someone else's
                    // parts, so read-only viewers are treated as non-practitioners here.
                    isPractitioner={isPractitioner && !isReadOnlyViewer}
                    forceShowClientFeatures={isOnboardingDemo}
                />
                {!isReadOnlyViewer && (
                    <FeedbackSection
                        sessionId={sessionId}
                        text="Do these parts resonate?"
                        feature="parts list"
                        variant="subtle"
                    />
                )}
            </div>
        );
        // AIDEV-NOTE: Only show the "Parts you worked with" heading when there are
        // focal parts; otherwise SessionParts renders its own section headings
        // (e.g. "Parts you mentioned") and a standalone empty title looks broken.
        const hasFocalParts = parts.present.some(
            (part) => part.presenceLevel?.toLowerCase().includes("focal"),
        );
        rightColumnTitle = hasFocalParts ? "Parts you worked with" : undefined;
    }

    // AIDEV-NOTE: Transcript is a reference, consulted occasionally — it does not earn a
    // tab beside the notes. It lives in the ••• menu and opens over the page.
    const transcriptModal = (
        <Dialog
            analyticsId="session-transcript"
            open={transcriptOpen}
            onOpenChange={setTranscriptOpen}
        >
            <DialogContent className="max-h-[85vh] max-w-4xl overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>Transcript</DialogTitle>
                </DialogHeader>
                <div className="flex justify-start">
                    <Button variant="ghost" onClick={copyTranscriptToClipboard}>
                        <Copy className="mr-2 size-4" />
                        Copy Transcript to Clipboard
                    </Button>
                </div>
                <Transcript sessionId={sessionId} />
            </DialogContent>
        </Dialog>
    );

    // AIDEV-NOTE: Session Notes redesign — date renders as an eyebrow directly
    // above the title (mockup 2). Passed as the Layout `title` so it sits in the
    // header row, left-aligned and top-aligned with the action buttons (the desktop
    // header H1 is left-aligned with an auto-spacer before actions). The standalone
    // body date row was removed.
    const title = (
        <div className="flex w-full flex-col items-start gap-1 text-left">
            {/* AIDEV-NOTE: Mobile-only "‹ Sessions" back link, stacked above the date
                eyebrow. Replaces the Layout's default mobile back arrow (hidden via
                hideBack). Desktop uses the sidebar for navigation. */}
            <Button
                asChild
                variant="chromeGhost"
                size="chrome"
                className="-ml-4 mb-3 self-start lg:hidden"
            >
                <Link to="/sessions">
                    <ChevronLeft />
                    Sessions
                </Link>
            </Button>
            <DateEditor
                sessionId={sessionId}
                currentDate={new Date(session.meetingStartTime)}
                prefix="Session on "
                eyebrow
            />
            <TitleEditor sessionId={sessionId} currentTitle={session.title} />
        </div>
    );

    return (
        <Layout
            fullWidth={true}
            data-testid="session-page"
            loading={!session}
            backNavOverride={"/sessions"}
            hideBack
            actions={sessionActions}
            sessionMenuItems={sessionMenuItems}
            stickyHeader={false}
            title={title}
            // AIDEV-NOTE: The consultant read-only notice deliberately does NOT
            // use headerBanner — that slot (DesktopLayout) renders full-bleed,
            // above and outside the page's own padded <header>, edge-to-edge
            // with no horizontal margin. It's the right slot for PromotionBanner
            // (styled rounded-none/border-x-0 specifically for that), but a
            // rounded, bordered card there collides with the container edge and
            // leaves dead space around it (Adam, 2026-09-03 — the exact bug this
            // fixes). Per DESIGN.md, a page-specific notice like this is an Alert
            // ("use Alert for inline messaging, Banner for page-level global
            // notifications"), not a Banner — so it renders as a normal Alert
            // inside the body margins, matching how the /consultation page
            // already renders its own consultant notice correctly.
            headerBanner={
                sessionToken ? (
                    <PromotionBanner />
                ) : isDemoSession ? (
                    <ExampleSessionBanner />
                ) : null
            }
        >
            {isConsultantViewer && (
                <Alert
                    variant="info"
                    className="mb-4 flex flex-wrap items-center gap-2.5"
                    data-testid="consultant-readonly-banner"
                >
                    {/* AIDEV-NOTE: the `info` Alert variant hides direct <svg>
                        children by design (design system spec: text-only
                        callouts); wrap the icon in a span so it renders. */}
                    <span className="shrink-0">
                        <Lock className="size-4 text-brand-forest-600" />
                    </span>
                    <span className="text-base font-medium text-brand-forest-800">
                        Editable by the client and practitioner — not by
                        consultants
                    </span>
                    <span className="inline-flex items-center rounded-full bg-white px-2.5 py-0.5 text-xs font-semibold text-brand-forest-600 ring-1 ring-brand-forest-200">
                        Read-only
                    </span>
                </Alert>
            )}
            {/* AIDEV-NOTE: Participants row is desktop-only now that the date is an
                eyebrow inside the title block (mockup 2 redesign). */}
            <div className="hidden w-full items-center justify-between gap-2 lg:mb-4 lg:flex">
                <div className="flex items-center gap-2">
                    <div className="hidden items-center lg:flex">
                        {session && showParticipantControls && (
                            <SessionParticipants
                                practitionerId={session.practitionerId}
                                practitionerEmail={session?.practitioner?.email}
                                clientId={session.clientId}
                                clientEmail={session?.client?.email}
                                clientName={session?.client?.fullName}
                                isPublicSession={isReadOnlyViewer}
                                readOnly={isDemoSession}
                                onSubmit={async ({
                                    practitionerId,
                                    practitionerEmail,
                                    clientId,
                                    clientEmail,
                                }) => {
                                    const promises: Promise<unknown>[] = [];
                                    if (
                                        practitionerId !==
                                        session.practitionerId
                                    ) {
                                        promises.push(
                                            updateSession({
                                                action: "update-practitioner",
                                                practitionerId,
                                                practitionerEmail,
                                            }),
                                        );
                                    }
                                    if (clientId !== session.clientId) {
                                        promises.push(
                                            updateSession({
                                                action: "update-client",
                                                clientId,
                                                clientEmail,
                                            }),
                                        );
                                    }
                                    await Promise.all(promises);
                                }}
                            />
                        )}
                    </div>
                </div>
            </div>
            {transcriptModal}
            {showSessionBillingBlockedBanner && billingBlocked && (
                <div className="mb-4">
                    <BillingSoftGate
                        variant="sessionNotes"
                        billing={billingBlocked}
                        analyticsSurface="session_notes_soft_gate"
                    />
                </div>
            )}
            {!showSessionBillingBlockedBanner &&
                billingEntitlement?.enforced &&
                billingEntitlement.entitlement?.usedUnits === 1 &&
                billingEntitlement.entitlement?.thresholdState === "active" &&
                session?.status === "COMPLETED" && (
                    <div className="mb-4">
                        <BillingAhaNudge />
                    </div>
                )}

            {/* AIDEV-NOTE: Print-only layout - shows summary + long summary + parts by category + detailed parts */}
            <PrintOnlyContent
                session={session}
                parts={parts}
                effectiveSettings={effectiveSettings}
            />

            {/* Regular layout for all tabs - hidden in print */}
            <div className="flex justify-between gap-12 print:hidden">
                <div className="max-w-1/2 flex w-1/2 grow flex-col gap-6">
                    <div className={"md:space-y-12"}>
                        <div className="mb-4 space-y-4 text-left text-lg md:mt-2">
                            <GenerateImagesBanner
                                partInteractions={parts.present}
                                sessionId={sessionId}
                                enabled={canDisplayPartImages}
                                clientId={
                                    session.clientId || session.uploadedByUserId
                                }
                                onComplete={refetch}
                            />

                            <div className="hidden lg:block">
                                <>
                                    <SessionSummary
                                        session={session}
                                        sessionId={sessionId}
                                        updateSessionSummary={
                                            updateSessionSummary
                                        }
                                        showFullSummary={
                                            tabState.showFullSummary
                                        }
                                        setShowFullSummary={(value) =>
                                            setTabState((prev) => ({
                                                ...prev,
                                                showFullSummary: value,
                                            }))
                                        }
                                        isPublicSession={isReadOnlyViewer}
                                        commitments={commitments}
                                    />
                                    {/* AIDEV-NOTE: Added mt-8 for spacing between short summary and parts map */}
                                    {aggregatedRelationships.length > 0 && (
                                        <div
                                            className="mt-8 space-y-6"
                                            data-tour-id="session-parts-map"
                                        >
                                            <ScopedPartsMap
                                                heading="Session Parts Map"
                                                ariaLabel="Session parts map format"
                                                parts={partsWithRelationships.map(
                                                    (p) => ({
                                                        id: p.partId || p.id,
                                                        name:
                                                            p.part.name ||
                                                            p.name,
                                                        type:
                                                            p.part.type ?? null,
                                                        // AIDEV-NOTE: Map nodes must respect the same
                                                        // part-image display gate as the sidebar/detail
                                                        // views — images off means no images anywhere.
                                                        image: canDisplayPartImages
                                                            ? Array.isArray(
                                                                  p.part_image,
                                                              )
                                                                ? p
                                                                      .part_image[0]
                                                                : p.part_image
                                                            : null,
                                                    }),
                                                )}
                                                edges={aggregatedRelationships}
                                                suspectedParts={
                                                    suspectedMapParts
                                                }
                                                allowSuspectedToggle={
                                                    allowSuspectedOnMap
                                                }
                                                size={500}
                                                onSelectPart={(partId) =>
                                                    handlePartClick({
                                                        partId,
                                                    } as unknown as TPartInteractionWithImage)
                                                }
                                            />
                                            <FeedbackSection
                                                sessionId={sessionId}
                                                text="Is this parts map helpful?"
                                                feature="parts graph"
                                                variant="subtle"
                                            />
                                        </div>
                                    )}
                                    {/* AIDEV-NOTE: Display commitments & follow-ups below the parts map - data pre-transformed in backend */}
                                    {commitments &&
                                        commitments.length > 0 &&
                                        showCommitments && (
                                            <div
                                                className="mt-8"
                                                data-tour-id="commitments"
                                            >
                                                <H2 className="mb-4">
                                                    Commitments & Follow-ups
                                                </H2>
                                                <CommitmentsList
                                                    commitments={commitments}
                                                    showFulfillButton={true}
                                                    hideSessionFields={true}
                                                    emptyStateMessage="No commitments from this session"
                                                />
                                            </div>
                                        )}
                                </>
                            </div>
                        </div>

                        {/* Mobile tab content */}
                        <div className="block lg:hidden">
                            {/* AIDEV-NOTE: Parts map on mobile, above summary heading */}
                            {aggregatedRelationships.length > 0 && (
                                <div
                                    className="mb-6 space-y-4"
                                    data-tour-id="session-parts-map"
                                >
                                    <ScopedPartsMap
                                        heading="Session Parts Map"
                                        ariaLabel="Session parts map format"
                                        parts={partsWithRelationships.map(
                                            (p) => ({
                                                id: p.partId || p.id,
                                                name: p.part.name || p.name,
                                                type: p.part.type ?? null,
                                                // AIDEV-NOTE: Same display gate as desktop map above.
                                                image: canDisplayPartImages
                                                    ? Array.isArray(
                                                          p.part_image,
                                                      )
                                                        ? p.part_image[0]
                                                        : p.part_image
                                                    : null,
                                            }),
                                        )}
                                        edges={aggregatedRelationships}
                                        suspectedParts={suspectedMapParts}
                                        allowSuspectedToggle={
                                            allowSuspectedOnMap
                                        }
                                        size={360}
                                        onSelectPart={(graphPartId) => {
                                            // AIDEV-NOTE: Resolve reusable partId from graph node to interaction ID for mobile part detail route
                                            const interaction =
                                                parts.present.find(
                                                    (p) =>
                                                        (p.partId || p.id) ===
                                                        graphPartId,
                                                );
                                            if (interaction) {
                                                navigate(
                                                    `/sessions/${sessionId}/parts/${interaction.id}`,
                                                );
                                            }
                                        }}
                                    />
                                </div>
                            )}
                            {/* AIDEV-NOTE: No heading here — SessionSummary renders its
                                own "Session Summary" H2, and this branch was printing a
                                second one right above it. */}
                            <SessionSummary
                                session={session}
                                sessionId={sessionId}
                                updateSessionSummary={updateSessionSummary}
                                showFullSummary={tabState.showFullSummary}
                                setShowFullSummary={(value) =>
                                    setTabState((prev) => ({
                                        ...prev,
                                        showFullSummary: value,
                                    }))
                                }
                                isPublicSession={isReadOnlyViewer}
                                commitments={commitments}
                            />
                            {/* AIDEV-NOTE: Parts come before commitments on mobile: a
                                phone reader scrolls one column, and the parts are what the
                                summary was just describing. Commitments are the thing you
                                leave with, so they close the column. Desktop keeps its own
                                order — the parts list lives in the right-hand column there. */}
                            <div className="mb-6">
                                {/* AIDEV-NOTE: Mirrors the desktop right column, which
                                    titles this list "Parts you worked with" — and on the
                                    same condition. Without focal parts SessionParts renders
                                    its own section headings ("Parts you mentioned") and a
                                    standalone title above them looks broken. */}
                                {parts.present.some(
                                    (part) =>
                                        part.presenceLevel
                                            ?.toLowerCase()
                                            .includes("focal"),
                                ) ? (
                                    <H2 className="mb-4 text-left">
                                        Parts you worked with
                                    </H2>
                                ) : null}
                                <SessionParts
                                    meditationsEnabledForClient={
                                        meditationsEnabledForClient
                                    }
                                    parts={{
                                        present: parts.present,
                                        other: parts.other,
                                        suspectedParts: parts.suspectedParts,
                                    }}
                                    handlePartClick={handlePartClick}
                                    sessionId={sessionId}
                                    partSimilarities={
                                        session?.rawReport?.partSimilarities
                                    }
                                    isPractitioner={
                                        isPractitioner && !isReadOnlyViewer
                                    }
                                    forceShowClientFeatures={isOnboardingDemo}
                                />
                                {!isReadOnlyViewer && (
                                    <Card className="mt-8 p-2">
                                        <FeedbackSection
                                            sessionId={sessionId}
                                            text="Does this list of parts feel right?"
                                            feature="parts list"
                                            variant="subtle"
                                        />
                                    </Card>
                                )}
                            </div>
                            {/* AIDEV-NOTE: Commitments render on mobile too. This
                                        block previously existed ONLY in the desktop branch
                                        above, so on a phone there was no route to
                                        commitments from a session at all — the /commitments
                                        subroute lives in the secondary sidebar, which is
                                        globally disabled. Gate and props are identical to
                                        the desktop copy; keep the two in step.
                                        The duplicate data-tour-id is safe: the demo tour
                                        resolves targets with querySelectorAll and picks the
                                        visible one, so the CSS-hidden branch is skipped. */}
                            {commitments &&
                                commitments.length > 0 &&
                                showCommitments && (
                                    <div
                                        className="mb-6 mt-8"
                                        data-tour-id="commitments"
                                    >
                                        <H2 className="mb-4 text-left">
                                            Commitments &amp; Follow-ups
                                        </H2>
                                        <CommitmentsList
                                            commitments={commitments}
                                            showFulfillButton={true}
                                            hideSessionFields={true}
                                            emptyStateMessage="No commitments from this session"
                                        />
                                    </div>
                                )}
                        </div>

                        <InternalFeature>
                            <div>
                                <H2>Pipeline Output</H2>
                                <Link
                                    to={
                                        isReadOnlyViewer
                                            ? `/sessions/${sessionId}/sharing/pipeline-output`
                                            : `/sessions/${sessionId}/pipeline-output`
                                    }
                                >
                                    <Button variant="internal">
                                        View pipeline output
                                    </Button>
                                </Link>
                            </div>
                        </InternalFeature>
                    </div>
                </div>
                {rightColumnContent && (
                    <div className="max-w-1/2 hidden w-1/2 pt-3 lg:block print:hidden">
                        {/* AIDEV-NOTE: Card wrapper only when an individual part is selected.
                            The parts-list view renders without a panel-level background; each
                            individual part item carries its own card styling instead. */}
                        <div
                            className={clsx(
                                "relative",
                                rightColumnContentType === "part-detail"
                                    ? // AIDEV-NOTE: part-detail keeps its own scroll/clip + panel chrome.
                                      // Chrome matches the crafted cards it sits beside: an 18px
                                      // radius, a forest-100 hairline and the `shadow-card` token
                                      // (the one written for part cards and commitments). It used
                                      // to be rounded-md on a forest-200 border with shadow-md —
                                      // sharper corners, a border a step darker, and a tighter,
                                      // heavier shadow than anything else in the column.
                                      "overflow-y-auto overflow-x-visible rounded-[18px] border border-brand-forest-100 bg-white px-12 py-8 pb-20 shadow-card"
                                    : // AIDEV-NOTE: parts-list must NOT clip — overflow-y-auto here was
                                      // cropping the part cards' soft shadow lift. No height
                                      // constraint exists, so nothing actually scrolled anyway.
                                      "overflow-visible",
                            )}
                        >
                            <div className="flex items-center justify-between">
                                {rightColumnTitle && (
                                    <H2 className="mb-0 grow gap-2">
                                        {rightColumnTitle}
                                    </H2>
                                )}
                                {/* AIDEV-NOTE: Action buttons hidden in print */}
                                <div className="flex gap-2 print:hidden">
                                    {rightColumnContentType === "part-detail" &&
                                        selectedPartInteraction &&
                                        canEditPartImage && (
                                            <Button
                                                variant="ghostIcon"
                                                size="icon"
                                                onClick={() =>
                                                    setIsImageModalOpen(true)
                                                }
                                                title="Edit Part Image"
                                                disabled={isLoading}
                                            >
                                                <ImageIcon className="size-6" />
                                            </Button>
                                        )}
                                    {rightColumnContentType === "part-detail" &&
                                        selectedPartInteraction && (
                                            <ManagePartDialog
                                                part={selectedPartInteraction}
                                                sessionParts={parts.present}
                                                sessionId={sessionId}
                                                sessionOwnerId={sessionOwnerId!}
                                                isPublicSession={
                                                    isReadOnlyViewer
                                                }
                                            />
                                        )}
                                    <Button
                                        onClick={() => {
                                            setRightColumnContentType(
                                                "parts-list",
                                            );
                                            setSelectedPartInteractionId(null);
                                        }}
                                        variant="ghostIcon"
                                        className={clsx(
                                            rightColumnContentType ===
                                                "parts-list" && "hidden",
                                        )}
                                    >
                                        <X className="size-6" />
                                    </Button>
                                </div>
                            </div>
                            {rightColumnContent}
                        </div>
                    </div>
                )}
            </div>
            {selectedPartInteraction && (
                <PartImageEditorModal
                    isOpen={isImageModalOpen && canDisplayPartImages}
                    onClose={() => setIsImageModalOpen(false)}
                    part={selectedPart}
                    partInteractionId={selectedPartInteractionId}
                />
            )}

            {/* Part Group Suggestion Dialog */}
            <PartGroupSuggestionDialog
                isOpen={isGroupSuggestionDialogOpen}
                onOpenChange={setIsGroupSuggestionDialogOpen}
                sessionId={sessionId}
                groupingType={groupSuggestionType}
                onComplete={() => {
                    setIsGroupSuggestionDialogOpen(false);
                }}
            />

            {/* Dialogs rendered outside dropdown - only for mobile hamburger menu */}
            <div className="block md:hidden">
                <UnifiedShareDialog
                    sessionId={sessionId}
                    showLinkOption={isPractitioner}
                    open={shareDialogOpen}
                    onOpenChange={setShareDialogOpen}
                />
            </div>

            {/* AIDEV-NOTE: Tour UI for session page onboarding */}
            <TourRoot tourId="session-tour" />
        </Layout>
    );
};
